import React from 'react';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import {
  HeartIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  DevicePhoneMobileIcon,
  BuildingLibraryIcon,
  UsersIcon,
  LightBulbIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  AcademicCapIcon,
  TruckIcon,
  BriefcaseIcon,
  HomeIcon,
  UserGroupIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useLanguageContext } from '../navigations/homenav';

// Donation Method Card Component
const DonationMethodCard = ({ icon, title, children }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
    <div className="flex flex-col items-center mb-4">
      <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white mb-2">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-800 text-center">{title}</h3>
    </div>
    <div className="space-y-3">
      {children}
    </div>
  </div>
);

// Impact Item Component
const ImpactItem = ({ icon, title, description }) => (
  <div className="flex items-start space-x-3">
    <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-teal-100 text-teal-600 flex-shrink-0">
      {icon}
    </div>
    <div>
      <h4 className="font-semibold text-gray-800 mb-1 text-sm">{title}</h4>
      <p className="text-gray-600 text-xs leading-relaxed">{description}</p>
    </div>
  </div>
);

const DonationPage = () => {
  const { t } = useLanguageContext();

  const impactItems = [
    {
      icon: <UsersIcon className="h-5 w-5" />,
      title: t('donateImpact1Title'),
      description: t('donateImpact1Desc')
    },
    {
      icon: <AcademicCapIcon className="h-5 w-5" />,
      title: t('donateImpact2Title'),
      description: t('donateImpact2Desc')
    },
    {
      icon: <GlobeAltIcon className="h-5 w-5" />,
      title: t('donateImpact3Title'),
      description: t('donateImpact3Desc')
    },
    {
      icon: <HandRaisedIcon className="h-5 w-5" />,
      title: t('donateImpact4Title'),
      description: t('donateImpact4Desc')
    },
    {
      icon: <LightBulbIcon className="h-5 w-5" />,
      title: t('donateImpact5Title'),
      description: t('donateImpact5Desc')
    }
  ];

  const donorImpactHighlights = [
    {
      icon: <TruckIcon className="h-5 w-5" />,
      title: t('donorImpactHighlight1'),
      description: t('donorImpactHighlight1Desc')
    },
    {
      icon: <HomeIcon className="h-5 w-5" />,
      title: t('donorImpactHighlight2'),
      description: t('donorImpactHighlight2Desc')
    },
    {
      icon: <UserGroupIcon className="h-5 w-5" />,
      title: t('donorImpactHighlight3'),
      description: t('donorImpactHighlight3Desc')
    },
    {
      icon: <BriefcaseIcon className="h-5 w-5" />,
      title: t('donorImpactHighlight4'),
      description: t('donorImpactHighlight4Desc')
    },
    {
      icon: <SparklesIcon className="h-5 w-5" />,
      title: t('donorImpactHighlight5'),
      description: t('donorImpactHighlight5Desc')
    }
  ];

  return (
    <div className="bg-gray-50">
      <HomeNav />

      <main>
        {/* Hero Section */}
        <section
          className="relative py-16 md:py-24 bg-cover bg-center text-white"
          style={{ backgroundImage: "url('/donateimage1.png')" }}
        >
          <div className="absolute inset-0 bg-slate-800 bg-opacity-70"></div>
          <div className="relative max-w-6xl mx-auto px-6 text-center z-10">
            <HeartIcon className="h-16 w-16 mx-auto mb-6 text-teal-400" />
            <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">{t('donationHeroTitle')}</h1>
            <p className="text-base md:text-lg text-teal-100 max-w-3xl mx-auto leading-relaxed mb-6">{t('donationHeroDesc1')}</p>
            <button
              className="inline-block px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-base rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              onClick={() => {
                const el = document.getElementById('how-to-donate');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {t('donateNow')}
            </button>
          </div>
        </section>

        {/* Donor Impact Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <HeartIcon className="h-16 w-16 mx-auto mb-6 text-teal-500" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('donorImpactTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
                {t('donorImpactSubtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {donorImpactHighlights.map((highlight, index) => (
                <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-teal-100">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 leading-tight">
                      {highlight.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Donate Section */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('whyDonateTitle')}</h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">{t('whyDonateDesc')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {impactItems.map((item, index) => (
                <ImpactItem key={index} {...item} />
              ))}
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-xl text-center">
              <CurrencyDollarIcon className="h-12 w-12 mx-auto text-teal-600 mb-3" />
              <h3 className="text-xl font-bold text-gray-800 mb-3">{t('contributionMattersTitle')}</h3>
              <p className="text-base text-gray-700 leading-relaxed">{t('contributionMattersDesc')}</p>
            </div>
          </div>
        </section>

        {/* How to Donate Section */}
        <section id="how-to-donate" className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('howToDonateTitle')}</h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto">{t('howToDonateDesc')}</p>
            </div>

            <div className="grid grid-cols-1 gap-6 justify-items-center mx-auto max-w-xl">
              {/* Bank Transfer */}
              <DonationMethodCard
                icon={<BuildingLibraryIcon className="h-6 w-6" />}
                title={t('bankTransferTitle')}
              >
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center mb-2">
                      <img src="/cbelogo.jpeg" alt="CBE Logo" className="h-8 w-8 mr-3 rounded" />
                      <h4 className="font-semibold text-gray-800 text-base">{t('cbeBankLabel')}</h4>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                      <p className="text-gray-700"><span className="font-medium">{t('accountNameLabel')}</span> Addis Ababa Disability Association</p>
                      <p className="text-gray-700"><span className="font-medium">{t('accountNumberLabel')}</span> 1000570772736 / 1000004335671</p>
                      <p className="text-gray-700"><span className="font-medium">{t('swiftCodeLabel')}</span> CBETETAA</p>
                    </div>
                  </div>

                </div>
              </DonationMethodCard>

              
            </div>

            <div className="mt-12 text-center">
              <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto">
                <BanknotesIcon className="h-12 w-12 mx-auto text-green-600 mb-3" />
                <h3 className="text-xl font-bold text-gray-800 mb-3">{t('thankYouTitle')}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{t('thankYouDesc')}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DonationPage;
